/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react'
import { isEmpty } from 'lodash'
import {
  mdiArrowLeft,
  mdiCircle,
  mdiCircleOutline,
  mdiPowerPlug,
} from '@mdi/js'
import Icon from '@mdi/react'
import { useIntl, FormattedMessage } from 'react-intl'
import { HomeId } from '@racepointenergy/swc/scs/types'
import { generatePath, RouteComponentProps, useHistory } from 'react-router-dom'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'

import { WhiteBlock } from 'components/Layout'
import { messages } from '../messages'
import {
  BreakerHeader,
  PanelInfoColumn,
  PanelInfoHeaderText,
  PanelInfoText,
  PanelInfoRow,
  StatusWrapper,
  NavBack,
  BreakerBlock,
  BreakerGrid,
  ChannelText,
} from '../styles'
import { SpinnerCentered } from 'components/Loader'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import DeviceUID from './DeviceUID'
import { H1 } from 'components/Typography'
import theme from 'theme_'
import ROUTES from 'app/routes'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import {
  fetchHomeActiveConfigEnergyThunk,
  homeActiveConfigEnergyActions,
} from 'features/home/components/HomeEnergySystem/slice'
import { Circuit, HW, Panel } from 'features/home/types'

interface ModuleInfoProps
  extends RouteComponentProps<{
    homeId: HomeId
    panelId: string
    moduleId: string
  }> {}

const hostStateInitialState = stateEntityAdapter.getInitialState()

function ModuleInfo({ match }: ModuleInfoProps) {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const [mappedStatesA, setMappedStatesA] = useState({} as HW)
  const [mappedStatesB, setMappedStatesB] = useState({} as HW)
  const [circuitA, setCircuitA] = useState({} as any)
  const [circuitB, setCircuitB] = useState({} as any)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [pbcName, setPbcName] = useState('-')
  const [pbcUID, setPbcUID] = useState('-')
  const [panelData, setPanelData] = useState<Panel | null>(null)
  const [panelSlots, setPanelSlots] = useState('-')

  const { homeId, panelId, moduleId } = match.params

  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const handleResize = () => {
    setIsMobile(window.matchMedia(theme.devices.mobile).matches)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      dispatch(homeActiveConfigEnergyActions.clear())
    }
  }, [dispatch, homeId])

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])

  useEffect(() => {
    if (homeActiveConfigEnergy) {
      // I'm sorry, this data is terrible
      const lightingDevice = homeActiveConfigEnergy?.lightingDevices?.find(
        (device: any) =>
          device.smartHardware.find((hw: any) => hw.hwUID === moduleId)
      )

      setPbcName(lightingDevice?.name ?? '-')
      setPbcUID(lightingDevice?.uid ?? '-')

      let chA = ''
      let chB = ''

      const areWithinOneDecimal = (num1: string, num2: string) => {
        let decimal1 = parseInt(num1, 16)
        let decimal2 = parseInt(num2, 16)

        let diff = Math.abs(decimal1 - decimal2)

        return num1.substring(0, 8) === num2.substring(0, 8) && diff === 1
      }

      if (parseInt(moduleId, 16) % 2 === 0) {
        chB = moduleId

        chA =
          lightingDevice?.smartHardware.find((hw: any) =>
            areWithinOneDecimal(hw.hwUID, moduleId)
          )?.hwUID ?? ''
      } else {
        chA = moduleId
        chB =
          lightingDevice?.smartHardware.find((hw: any) =>
            areWithinOneDecimal(hw.hwUID, moduleId)
          )?.hwUID ?? ''
      }

      const mappedStatesA: HW = {}
      const mappedStatesB: HW = {}

      if (chA) {
        const states = Object.keys(data.entities)
          .filter((key) => key.includes(chA))
          .reduce(
            (obj, key) => {
              obj[key as keyof {}] = data.entities[key]
              return obj
            },
            {} as {
              [key: string]: any
            }
          )

        Object.keys(states).forEach((key: string) => {
          const splitKey = key.split('.')
          const address = splitKey[splitKey.length - 1].split('_')
          if (address[1] === lightingDevice?.address) {
            mappedStatesA[`${address[0]}` as keyof HW] = states[key]
          }
        })
      }
      if (chB) {
        const states = Object.keys(data.entities)
          .filter((key) => key.includes(chB))
          .reduce(
            (obj, key) => {
              obj[key as keyof {}] = data.entities[key]
              return obj
            },
            {} as {
              [key: string]: any
            }
          )

        Object.keys(states).forEach((key: string) => {
          const splitKey = key.split('.')
          const address = splitKey[splitKey.length - 1].split('_')
          if (address[1] === lightingDevice?.address) {
            mappedStatesB[`${address[0]}` as keyof HW] = states[key]
          }
        })
      }

      const deviceConfig = lightingDevice?.smartHardware.find(
        (hw: any) => hw.hwUID === chA
      )?.olaConfig

      let slots = 3
      if (
        panelData?.panelType === 'Cutler-Hammer Type BR' ||
        panelData?.panelType === 'Square D QO' ||
        panelData?.panelType === 'Square D'
      ) {
        slots = 4
      }

      if (
        deviceConfig &&
        panelData?.slidesNumber === 'Dual' &&
        panelData?.panelType
      ) {
        if (deviceConfig.DeviceModelID !== 8) {
          setPanelSlots(
            `${deviceConfig.SlotNumber} - ${deviceConfig.SlotNumber + 2}`
          )
        } else {
          setPanelSlots(
            `${deviceConfig.SlotNumber} - ${
              deviceConfig.SlotNumber + 2 * (slots - 1)
            }`
          )
        }
      } else if (
        deviceConfig &&
        panelData?.slidesNumber === 'Single' &&
        panelData?.panelType
      ) {
        if (deviceConfig.DeviceModelID !== 8) {
          setPanelSlots(
            `${deviceConfig.SlotNumber} - ${deviceConfig.SlotNumber + 1}`
          )
        } else {
          setPanelSlots(
            `${deviceConfig.SlotNumber} - ${
              deviceConfig.SlotNumber + slots - 1
            }`
          )
        }
      }

      setMappedStatesA(mappedStatesA)
      setMappedStatesB(mappedStatesB)

      if (
        homeActiveConfigEnergy?.energyConfig?.circuits?.length &&
        data?.entities
      ) {
        if (mappedStatesA) {
          const circuitName =
            homeActiveConfigEnergy.energyConfig?.circuits?.find((circuit) =>
              circuit.companion?.includes(chA)
            )?.name
          setCircuitA({
            name: circuitName,
            critical: data.entities[`Energy.Circuit.${circuitName}.Status`]
              ?.value
              ? (data.entities[`Energy.Circuit.$circuitName}.Status`]?.value &
                  0b0001) ===
                1
                ? formatMessage(messages.yes)
                : formatMessage(messages.no)
              : '-',
            status:
              data.entities[`Energy.Circuit.${circuitName}.Status`]?.value,
          })
        }
        if (mappedStatesB) {
          const circuitName =
            homeActiveConfigEnergy.energyConfig?.circuits?.find((circuit) =>
              circuit.companion?.includes(chB)
            )?.name
          setCircuitB({
            name: circuitName,
            critical: data.entities[`Energy.Circuit.${circuitName}.Status`]
              ?.value
              ? (data.entities[`Energy.Circuit.${circuitName}.Status`]?.value &
                  0b0001) ===
                1
                ? formatMessage(messages.yes)
                : formatMessage(messages.no)
              : '-',
            status:
              data.entities[`Energy.Circuit.${circuitName}.Status`]?.value,
          })
        }
      }
    }
  }, [homeActiveConfigEnergy, moduleId, data, panelData, formatMessage])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      const e =
        homeActiveConfigEnergy.energyConfig?.panelConfig.breakerPanels.find(
          (panel) => panel.id === panelId
        )
      if (e) {
        setPanelData(e)
      }
    }
  }, [homeActiveConfigEnergy, setPanelData, panelId, panelData?.slotsNumber])

  const onBackClick = () => {
    const devicesPath = generatePath(ROUTES.breakerPanel, {
      rootDomainId: currentRootId || '',
      homeId: homeId || ' ',
      panelId: panelId || '',
    })

    return history.push(devicesPath)
  }

  const icon = (status: number) => {
    if (status) {
      if ((status & 0b100) === 1) {
        return (
          <Icon
            path={mdiPowerPlug}
            size={0.5}
            color={theme.colors.otterWarmBlack}
          />
        )
      } else if ((status & 0b1) === 1) {
        return (
          <Icon path={mdiCircle} size={0.5} color={theme.colors.successGreen} />
        )
      }

      return (
        <Icon
          path={mdiCircleOutline}
          size={0.5}
          color={theme.colors.horizonOrange}
        />
      )
    }
  }

  const status = (status: number) => {
    if (status) {
      if ((status & 0b100) === 1) {
        return 2
      } else if ((status & 0b1) === 1) {
        return 1
      }

      return 0
    }
  }

  return (
    <div>
      <NavBack onClick={onBackClick}>
        <Icon path={mdiArrowLeft} size={1} />
        <FormattedMessage {...messages.backToDevices} />
      </NavBack>
      <WhiteBlock>
        {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
          <SpinnerCentered />
        ) : (
          <>
            <BreakerHeader>
              <H1>{panelData?.panelName}</H1>
              {circuitA?.status && (
                <StatusWrapper status={(circuitA?.status & 0b1000000) !== 1}>
                  <Icon
                    path={
                      (circuitA?.status & 0b1000000) === 1
                        ? mdiCircleOutline
                        : mdiCircle
                    }
                    size={1}
                    color={
                      (circuitA?.status & 0b1000000) === 1
                        ? theme.colors.horizonOrange
                        : theme.colors.successGreen
                    }
                  />
                  {(circuitA?.status & 0b1000000) === 1 ? (
                    <FormattedMessage {...messages.offline} />
                  ) : (
                    <FormattedMessage {...messages.online} />
                  )}
                </StatusWrapper>
              )}
            </BreakerHeader>

            <PanelInfoRow>
              <PanelInfoColumn>
                <PanelInfoText>
                  <DeviceUID uid={moduleId} />
                </PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.headerDeviceType} />
                </PanelInfoHeaderText>
                <PanelInfoText>
                  {mappedStatesA.DeviceModelDescription?.value ?? '-'}
                </PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.hardwareGeneration} />
                </PanelInfoHeaderText>
                <PanelInfoText>
                  {mappedStatesA.CompanionGeneration?.value ?? '-'}
                </PanelInfoText>
              </PanelInfoColumn>
            </PanelInfoRow>
          </>
        )}
      </WhiteBlock>

      <BreakerGrid>
        <BreakerBlock>
          {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
            <SpinnerCentered />
          ) : (
            <>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.panel} />
                </PanelInfoHeaderText>
                <PanelInfoText>{panelData?.panelName}</PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.slotNumber} />
                </PanelInfoHeaderText>
                <PanelInfoText>{panelSlots}</PanelInfoText>
              </PanelInfoColumn>
            </>
          )}
        </BreakerBlock>
        <BreakerBlock>
          {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
            <SpinnerCentered />
          ) : (
            <>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <div>
                    <FormattedMessage {...messages.headerChannel} /> A
                  </div>
                  {circuitA?.status && (
                    <StatusWrapper status={status(circuitA?.status) === 1}>
                      {icon(circuitA?.status)}
                      <ChannelText>
                        {status(circuitA?.status) === 0 && (
                          <FormattedMessage {...messages.off} />
                        )}
                        {status(circuitA?.status) === 1 && (
                          <FormattedMessage {...messages.on} />
                        )}
                        {status(circuitA?.status) === 2 && (
                          <FormattedMessage {...messages.critical} />
                        )}
                      </ChannelText>
                    </StatusWrapper>
                  )}
                </PanelInfoHeaderText>
                <PanelInfoText>{circuitA?.name ?? '-'}</PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.critical} />
                </PanelInfoHeaderText>
                <PanelInfoText>{circuitA?.critical}</PanelInfoText>
              </PanelInfoColumn>
            </>
          )}
        </BreakerBlock>

        {!isEmpty(mappedStatesB) && (
          <BreakerBlock>
            {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
              <SpinnerCentered />
            ) : (
              <>
                <PanelInfoColumn>
                  <PanelInfoHeaderText>
                    <div>
                      <FormattedMessage {...messages.headerChannel} /> B
                    </div>
                    {circuitA?.status && (
                      <StatusWrapper status={status(circuitB?.status) === 1}>
                        {icon(circuitB?.status)}
                        <ChannelText>
                          {status(circuitB?.status) === 0 && (
                            <FormattedMessage {...messages.off} />
                          )}
                          {status(circuitB?.status) === 1 && (
                            <FormattedMessage {...messages.on} />
                          )}
                          {status(circuitB?.status) === 2 && (
                            <FormattedMessage {...messages.critical} />
                          )}
                        </ChannelText>
                      </StatusWrapper>
                    )}
                  </PanelInfoHeaderText>
                  <PanelInfoText>{circuitB?.name ?? '-'}</PanelInfoText>
                </PanelInfoColumn>
                <PanelInfoColumn>
                  <PanelInfoHeaderText>
                    <FormattedMessage {...messages.critical} />
                  </PanelInfoHeaderText>
                  <PanelInfoText>{circuitB?.critical}</PanelInfoText>
                </PanelInfoColumn>
              </>
            )}
          </BreakerBlock>
        )}
      </BreakerGrid>

      <BreakerGrid>
        <BreakerBlock>
          {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
            <SpinnerCentered />
          ) : (
            <>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.firmwareVersion} />
                </PanelInfoHeaderText>
                <PanelInfoText>
                  {mappedStatesA.Version?.value ?? '-'}
                </PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.firmwareUpgrade} />
                </PanelInfoHeaderText>
                <PanelInfoText>
                  {mappedStatesA.Version?.value !==
                  mappedStatesA.VersionAvailable?.value
                    ? mappedStatesA.VersionAvailable?.value
                    : '-'}
                </PanelInfoText>
              </PanelInfoColumn>
            </>
          )}
        </BreakerBlock>
        <BreakerBlock>
          {isFetching && isEmpty(mappedStatesA) && isEmpty(mappedStatesB) ? (
            <SpinnerCentered />
          ) : (
            <>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.pairedPBCName} />
                </PanelInfoHeaderText>
                <PanelInfoText>{pbcName}</PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.pairedPBCUID} />
                </PanelInfoHeaderText>
                <PanelInfoText>{pbcUID}</PanelInfoText>
              </PanelInfoColumn>
            </>
          )}
        </BreakerBlock>
      </BreakerGrid>
    </div>
  )
}

export default ModuleInfo
